import { Alert, Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetBusinessAddons } from '@/apis/business/addons/hook';
import { BusinessAddon } from '@/apis/business/addons/types';
import { useApEmbedIframeUrl } from '@/apis/business/setting/activepieces/hook';
import { getStoredApEmbedToken, shouldRefreshApEmbedToken } from '@/apis/business/setting/activepieces/session';
import ActivateAddonDrawer from '@/pages/Business/Setting/Addons/components/ActivateAddonDrawer';
import { useAiWorkflowAddonActive } from '@/pages/Business/Setting/hooks/useAiWorkflowAddonActive';

export default function Workflows() {
  const theme = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();
  const embedKey = (location.state as { embedKey?: number } | null)?.embedKey ?? 0;
  const [reloadKey, setReloadKey] = useState(0);
  const [activateAddon, setActivateAddon] = useState<BusinessAddon | null>(null);

  const { aiWorkflowActive, isLoading: isAddonLoading } = useAiWorkflowAddonActive();
  const { data: addonsData } = useGetBusinessAddons();
  const aiWorkflowAddon = (addonsData?.data ?? []).find((addon) => addon.code === 'ai_workflow') ?? null;

  const {
    data: iframeUrl,
    isLoading,
    isFetching,
    isError,
    error
  } = useApEmbedIframeUrl({
    hideFlags: {
      hideSideNav: false,
      hidePageHeader: true,
      hideHomeButtonInBuilder: true,
      hideFlowsPageNavbar: true,
      hideGlobalSearch: true,
      hideActiveUsers: true
    }
  });

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!shouldRefreshApEmbedToken(getStoredApEmbedToken())) return;

      queryClient.removeQueries({ queryKey: ['ap_embed_iframe_url'] });
      setReloadKey((key) => key + 1);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [queryClient]);

  const showSpinner = isLoading || isFetching;
  const showInactiveBanner = !isAddonLoading && !aiWorkflowActive;

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0} gap={2}>
      {showInactiveBanner && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            px: 2.5,
            py: 3,
            borderRadius: 2,
            bgcolor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
            flexShrink: 0
          }}
        >
          <Box flex={1} minWidth={0}>
            <Typography fontSize={18} fontWeight={500} color={theme.palette.warning.main} mb={0.5}>
              AI Workflow Add-On Is Not Activated Yet
            </Typography>
            <Typography fontSize={14} color='#777777' lineHeight={1.5}>
              Your workflows are saved but not performing any action. Activate the add-on to start running automations.
            </Typography>
          </Box>
          <Button
            variant='contained'
            disabled={!aiWorkflowAddon}
            onClick={() => {
              if (aiWorkflowAddon) setActivateAddon(aiWorkflowAddon);
            }}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              flexShrink: 0,
              textTransform: 'none',

              px: 2.5,
              minHeight: 36,
              bgcolor: theme.palette.warning.main,
              boxShadow: 'none',
              '&:hover': { bgcolor: theme.palette.warning.main, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#FDBA74', color: '#fff' }
            }}
          >
            Activate
          </Button>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: { xs: '70vh', lg: 'calc(100vh - 140px)' },
          border: '1px solid #ECECEC',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {showSpinner && <CircularProgress />}
        {isError && !showSpinner && (
          <Alert severity='error' sx={{ m: 3 }}>
            Failed to load Workflows: {error?.message ?? 'Unknown error'}
          </Alert>
        )}
        {iframeUrl && !showSpinner && (
          <iframe
            key={`${embedKey}-${reloadKey}-${iframeUrl}`}
            src={iframeUrl}
            title='Workflows'
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block'
            }}
            allow='clipboard-read; clipboard-write'
          />
        )}
      </Box>

      <ActivateAddonDrawer addon={activateAddon} open={Boolean(activateAddon)} onClose={() => setActivateAddon(null)} />
    </Box>
  );
}
